import {
  DB_CATEGORY_TO_AREA,
  JOB_CATEGORY_AREAS,
  ROLE_AREA_OVERRIDES,
} from 'constants/jobCategoryAreas';
import { getRoleCategory } from 'helpers/Constants';
import { toPlural, toSeoUrl } from 'helpers/HelperFunctions';

/**
 * Resolve which public area a suggested role belongs to.
 * Order: API category → Constants roleCategories → ROLE_AREA_OVERRIDES.
 */
export function resolveRoleAreaId(role) {
  if (!role || !role.name) {
    return null;
  }

  if (role.category && DB_CATEGORY_TO_AREA[role.category]) {
    return DB_CATEGORY_TO_AREA[role.category];
  }

  const fromConstants = getRoleCategory(role.name);
  if (fromConstants && DB_CATEGORY_TO_AREA[fromConstants]) {
    return DB_CATEGORY_TO_AREA[fromConstants];
  }

  if (ROLE_AREA_OVERRIDES[role.name]) {
    return ROLE_AREA_OVERRIDES[role.name];
  }

  return null;
}

export function roleJobHref(role) {
  if (role.slug_plural) {
    return `/jobs/${role.slug_plural}`;
  }
  return `/jobs/${toSeoUrl(toPlural(role.name))}`;
}

/**
 * Group suggested roles into the five public areas.
 * Each role appears at most once. Roles that cannot be placed are returned in
 * `unplaced` so callers can flag them.
 */
export function groupRolesByArea(roles = []) {
  const buckets = {};
  JOB_CATEGORY_AREAS.forEach((area) => {
    buckets[area.id] = [];
  });

  const unplaced = [];
  const seen = new Set();

  roles.forEach((role) => {
    if (!role || !role.name || seen.has(role.name)) {
      return;
    }
    seen.add(role.name);

    const areaId = resolveRoleAreaId(role);
    const entry = {
      name: role.name,
      href: roleJobHref(role),
    };

    if (areaId && buckets[areaId]) {
      buckets[areaId].push(entry);
    } else {
      unplaced.push(entry);
    }
  });

  // Stable alphabetical order within each area
  Object.keys(buckets).forEach((id) => {
    buckets[id].sort((a, b) => a.name.localeCompare(b.name));
  });
  unplaced.sort((a, b) => a.name.localeCompare(b.name));

  const areas = JOB_CATEGORY_AREAS.map((area) => ({
    ...area,
    roles: buckets[area.id],
  }));

  return { areas, unplaced };
}
